import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { IUser } from './_models/user';
import { concatMap, forkJoin, Observable, of, tap, throwError } from 'rxjs';
import { WebinarService } from './_services/webinar.service';
import { catchError, finalize, map } from 'rxjs/operators';
import { UserService } from './_services/user.service';
import { Navigate } from '@ngxs/router-plugin';
import { RouteSlug } from './_models/route-slug.enum';
import {
  convertWarsawDateStringToCurrentTimezoneDate,
  jsonToHtml,
  LanguageEnum,
  WebinarType,
} from '@loreal-doctors-video-platform/data';
import { LANGUAGE_KEY } from './app.consts';
import { ISpecializationDropdownDataItem } from './_models/specialization-dropdown-data-item';
import { IWebinarListItem } from './_models/webinar/webinar-list-item';
import { IWebinarDetails } from './_models/webinar/webinar-details';
import { IWebinarClientListItem } from './_models/webinar/webinar-client-list-item';
import { WebinarProgressStatus } from './_models/webinar/webinar-progress-status.enum';
import { saveAs } from 'file-saver';
import { IWebinarDownloadDetails } from './_models/webinar/webinar-download-details';
import { IWebinarDetailsForLoggedUser } from './_models/webinar/webinar-details-for-logged-user';
import { environment } from '../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { snakeCase } from 'lodash-es';
import { ConferencesService } from './pages/conferences/conferences.service';
import { IWebinarCategory } from './_models/webinar/webinar-category';
import { IWebinarListParams } from './_models/webinar/webinar-list-params';
import { isBefore } from 'date-fns';

export function getDefaultLanguage(): LanguageEnum {
  const localStoreLanguage = localStorage.getItem(LANGUAGE_KEY)?.replace(/(['"])/g, '') as LanguageEnum;
  if (localStoreLanguage) {
    return localStoreLanguage;
  }

  const userLocaleLanguage = window.navigator.language.split(/[-_]/)[0];
  const availableLanguages = [LanguageEnum.PL.valueOf(), LanguageEnum.EN.valueOf()];
  if (availableLanguages.includes(userLocaleLanguage)) {
    return userLocaleLanguage as LanguageEnum;
  }

  return LanguageEnum.PL;
}

const CATEGORY_ORDER: number[] = [1, 3, 5, 2, 4, 6]; // Dermoskopia, Trichoskopia, Inflamoskopia, Atopia, Trądzik, Dermatologia pediatryczna

export class AppStateSetJwtToken {
  public static readonly type = '[AppState] set jwt token';

  constructor(public readonly payload: { token: string | null }) {}
}

export class AppStateLogout {
  public static readonly type = '[AppState] logout';
}

export class AppStateClearUserData {
  public static readonly type = '[AppState] clear user data';
}

export class AppStateGetUserData {
  public static readonly type = '[AppState] get user data';
}

export class AppStateSetUserData {
  public static readonly type = '[AppState] set user data';

  constructor(public readonly payload: { user: IUser }) {}
}

export class AppStateSetLanguage {
  public static readonly type = '[AppState] set language';

  constructor(public readonly payload: { language: LanguageEnum }) {}
}

export class AppStateFetchSpecializations {
  public static readonly type = '[AppState] fetch specializations';
}

export class AppStateFetchCategories {
  public static readonly type = '[AppState] fetch categories';
}

export class AppStateFetchWebinarList {
  public static readonly type = '[AppState] fetch webinar list';
}

export class AppStateFetchClientWebinarList {
  public static readonly type = '[AppState] fetch client webinar list';

  constructor(public readonly payload: { refreshWebinarList: boolean }) {}
}

export class AppStateRegisterLoggedClientToWebinar {
  public static readonly type = '[AppState] register logged client to webinar';

  constructor(public readonly payload: { slug: string }) {}
}

export class AppStateFindWebinarDetailsBySlug {
  public static readonly type = '[AppState] find webinar details by slug';

  constructor(public readonly payload: { slug: string }) {}
}

export class AppStateFindWebinarDetailsForLoggedClientBySlug {
  public static readonly type = '[AppState] find webinar details for logged client by slug';

  constructor(public readonly payload: { slug: string }) {}
}

export class AppStateClearWebinar {
  public static readonly type = '[AppState] clear webinar ';
}

export class AppStateDownloadFileOfWebinar {
  public static readonly type = '[AppState] download file of webinar';

  constructor(public readonly payload: { slug: string; webinarDownloadDetails: IWebinarDownloadDetails }) {}
}

export class AppStateDownloadWebinarCertificate {
  public static readonly type = '[AppState] download webinar certificate';

  constructor(public readonly payload: { slug: string; language: LanguageEnum }) {}
}

export class AppStateFetchWebinarLibrary {
  public static readonly type = '[AppState] fetch available webinar library';
  constructor(public params: IWebinarListParams = {}) {}
}

export class AppStateFetchDermasterclassWebinars {
  public static readonly type = '[AppState] fetch dermasterclass webinars';
  constructor(public params: IWebinarListParams = {}) {}
}

export interface AppStateModel {
  loading: boolean;
  cookiesAllowed: boolean | null;
  token: string | null;
  user: IUser | null;
  language: LanguageEnum;
  specializations: ISpecializationDropdownDataItem[] | null;
  webinars: IWebinarListItem[] | null;
  promotedWebinars: IWebinarListItem[] | null;
  webinarLibrary: IWebinarClientListItem[] | null;
  categories: IWebinarCategory[] | null;
  clientWebinars: IWebinarClientListItem[];
  clientWebinarsLoaded: boolean;
  dermasterclassWebinars: IWebinarListItem[] | null;
  webinar: IWebinarDetails | null;
  webinarDetailsForLoggedUser: IWebinarDetailsForLoggedUser | null;
  webinarSlug: string | null;
  conferences: any | null;
}

@State<AppStateModel>({
  name: 'app',
  defaults: {
    language: getDefaultLanguage(),
    loading: false,
    cookiesAllowed: null,
    token: null,
    user: null,
    specializations: null,
    webinars: null,
    webinarLibrary: null,
    promotedWebinars: null,
    dermasterclassWebinars: null,
    categories: null,
    clientWebinars: [],
    clientWebinarsLoaded: false,
    webinar: null,
    webinarDetailsForLoggedUser: null,
    webinarSlug: null,
    conferences: null,
  },
})
@Injectable()
export class AppState {
  constructor(
    @Inject(LOCALE_ID)
    public readonly locale: string,
    private readonly _dictionariesService: WebinarService,
    private readonly _conferencesService: ConferencesService,
    private readonly _userService: UserService,
  ) {}

  @Selector()
  public static loading({ loading }: AppStateModel): boolean {
    return loading;
  }

  @Selector()
  public static token({ token }: AppStateModel): string | null {
    return token;
  }

  @Selector()
  public static isLoggedToWebinar({ token }: AppStateModel): boolean {
    if (!token) {
      return false;
    }
    const jwtHelperService: JwtHelperService = new JwtHelperService();
    const decodedToken = jwtHelperService.decodeToken(token);
    return decodedToken.role === 'ROLE_CLIENT_LOGIN_WEBINAR';
  }

  @Selector()
  public static user({ user }: AppStateModel): IUser | null {
    return user;
  }

  @Selector()
  public static language({ language }: AppStateModel): LanguageEnum {
    return language;
  }

  @Selector()
  public static specializations({ specializations }: AppStateModel): ISpecializationDropdownDataItem[] | null {
    return specializations;
  }

  @Selector()
  public static categories({ categories }: AppStateModel): IWebinarCategory[] | null {
    return categories;
  }

  @Selector()
  public static webinars({ webinars }: AppStateModel): IWebinarListItem[] | null {
    return webinars;
  }

  @Selector()
  public static promotedWebinars(state: AppStateModel): IWebinarListItem[] | null {
    return state.promotedWebinars;
  }

  @Selector()
  public static clientWebinars({ clientWebinars }: AppStateModel): IWebinarClientListItem[] {
    return clientWebinars;
  }

  @Selector()
  public static dermasterclassWebinars({ dermasterclassWebinars }: AppStateModel): IWebinarListItem[] | null {
    return dermasterclassWebinars;
  }

  @Selector()
  public static webinar({ webinar }: AppStateModel): IWebinarDetails | null {
    return webinar;
  }

  @Selector()
  public static webinarDetailsForLoggedUser({
    webinarDetailsForLoggedUser,
  }: AppStateModel): IWebinarDetailsForLoggedUser | null {
    return webinarDetailsForLoggedUser;
  }

  @Selector()
  public static webinarSlug({ webinarSlug }: AppStateModel): string | null {
    return webinarSlug;
  }

  @Selector()
  public static recentlyAdded({ clientWebinars }: AppStateModel): IWebinarClientListItem[] | null {
    if (!clientWebinars) return null;
    const now = new Date();
    return clientWebinars.filter((clientWebinar) => {
      const { type, start_date, end_date } = clientWebinar.webinar;

      if (
        type !== WebinarType.NO_REGISTER &&
        type !== WebinarType.SAVED_WEBINAR &&
        type !== WebinarType.REGISTER_UNTIL_START
      )
        return false;

      const startDate = convertWarsawDateStringToCurrentTimezoneDate(start_date);
      const endDate = convertWarsawDateStringToCurrentTimezoneDate(end_date);
      return now.getTime() >= startDate.getTime() && now.getTime() < endDate.getTime();
    });
  }

  @Selector()
  public static webinarLibrary(state: AppStateModel): IWebinarClientListItem[] | null {
    return state.webinarLibrary;
  }

  @Selector()
  public static availableLibrary({ webinarLibrary }: AppStateModel): IWebinarListItem[] | null {
    if (!webinarLibrary) return null;
    const now = new Date();

    return webinarLibrary
      .filter((item) => {
        const { type, start_date, end_date } = item.webinar;
        const hasStarted = now.getTime() >= convertWarsawDateStringToCurrentTimezoneDate(start_date).getTime();
        const hasNotEnded = now.getTime() < convertWarsawDateStringToCurrentTimezoneDate(end_date).getTime();

        if (type === WebinarType.NO_REGISTER || type === WebinarType.REGISTER_UNTIL_START) {
          return hasStarted && hasNotEnded;
        }

        if (type === WebinarType.SAVED_WEBINAR) {
          return hasStarted && hasNotEnded && item.registered;
        }

        return false;
      })
      .map((item) => ({
        ...item.webinar,
        video_watched: item.video_watched,
      }));
  }

  @Selector()
  public static continueWatchingWebinars({ clientWebinars }: AppStateModel): IWebinarClientListItem[] | null {
    if (!clientWebinars) return null;
    const now = new Date();
    return clientWebinars.filter((item) => {
      // `progress` only carries a status set by the player view, the webinar-level
      // flag is raised once every video of the webinar has been watched.
      if (item.progress !== WebinarProgressStatus.IN_PROGRESS || item.video_watched === true) return false;

      const endDate = convertWarsawDateStringToCurrentTimezoneDate(item.webinar.end_date);
      return now.getTime() < endDate.getTime();
    });
  }

  @Selector()
  public static upcomingWebinars({ clientWebinars }: AppStateModel): IWebinarClientListItem[] | null {
    if (!clientWebinars) return null;
    const now = new Date();
    return clientWebinars.filter((webinar) => {
      if (
        webinar.webinar.type !== WebinarType.NO_REGISTER &&
        webinar.webinar.type !== WebinarType.SAVED_WEBINAR &&
        webinar.webinar.type !== WebinarType.REGISTER_UNTIL_START
      )
        return false;
      const startDate = convertWarsawDateStringToCurrentTimezoneDate(webinar.webinar.start_date);
      return !isBefore(startDate, now);
    });
  }

  @Selector()
  public static finishedWebinars({ clientWebinars }: AppStateModel): IWebinarClientListItem[] | null {
    if (!clientWebinars) return null;
    const now = new Date();
    return clientWebinars
      .filter((item) => {
        const { type, end_date } = item.webinar;

        if (
          type !== WebinarType.NO_REGISTER &&
          type !== WebinarType.SAVED_WEBINAR &&
          type !== WebinarType.REGISTER_UNTIL_START
        )
          return false;

        const endDate = convertWarsawDateStringToCurrentTimezoneDate(end_date);
        return isBefore(endDate, now);
      })
      .sort((a, b) => {
        const dateA = convertWarsawDateStringToCurrentTimezoneDate(a.webinar.end_date).getTime();
        const dateB = convertWarsawDateStringToCurrentTimezoneDate(b.webinar.end_date).getTime();
        return dateB - dateA;
      });
  }

  @Selector()
  public static completedDermasterclassWebinars({ clientWebinars }: AppStateModel): IWebinarClientListItem[] | null {
    if (!clientWebinars) return null;
    return clientWebinars.filter((item) => item.webinar.dermaster_class && item.video_watched === true);
  }

  @Action(AppStateSetUserData)
  public setUserData({ patchState }: StateContext<AppStateModel>, { payload: { user } }: AppStateSetUserData): void {
    patchState({
      user,
    });
  }

  @Action(AppStateSetLanguage)
  public setLanguage(
    { patchState, getState }: StateContext<AppStateModel>,
    { payload: { language } }: AppStateSetLanguage,
  ): void {
    if (language === getState().language) {
      return;
    }
    patchState({
      language,
    });
    location.reload();
  }

  @Action(AppStateGetUserData)
  public getUserData({ patchState }: StateContext<AppStateModel>): Observable<[IUser, IWebinarClientListItem[]]> {
    patchState({ loading: true });
    return forkJoin([this._userService.getMe(), this._userService.fetchClientWebinars()]).pipe(
      tap(([user, clientWebinars]) => {
        patchState({
          user,
          clientWebinars,
          clientWebinarsLoaded: true,
        });
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(AppStateSetJwtToken)
  public setJwtToken({ patchState }: StateContext<AppStateModel>, { payload: { token } }: AppStateSetJwtToken): void {
    patchState({ token });
  }

  @Action(AppStateLogout)
  public logout({ patchState, dispatch, getState }: StateContext<AppStateModel>): Observable<void | null> {
    const { token } = getState();
    if (!token) {
      patchState({ user: null, token: null, clientWebinars: [], clientWebinarsLoaded: false });
      dispatch(new Navigate([RouteSlug.AUTH, RouteSlug.LOGIN]));
      return of(null);
    }
    patchState({ loading: true });
    return this._userService.logout().pipe(
      tap(() => {
        patchState({ user: null, token: null, clientWebinars: [], clientWebinarsLoaded: false, loading: false });
        dispatch(new Navigate([RouteSlug.AUTH, RouteSlug.LOGIN]));
      }),
      catchError((err) => {
        patchState({ user: null, token: null, clientWebinars: [], clientWebinarsLoaded: false, loading: false });
        dispatch(new Navigate([RouteSlug.AUTH, RouteSlug.LOGIN]));
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateClearUserData)
  public clearUserData({ patchState }: StateContext<AppStateModel>): void {
    patchState({ user: null, token: null, clientWebinars: [], clientWebinarsLoaded: false });
  }

  @Action(AppStateFetchSpecializations)
  public fetchSpecializations({
    patchState,
    getState,
  }: StateContext<AppStateModel>): Observable<ISpecializationDropdownDataItem[]> {
    if (getState().specializations) {
      return of(getState().specializations || []);
    }
    patchState({ loading: true });

    const excludedSpecializations = ['Pielęgniarstwo', 'Położnictwo', 'Midwifery', 'Nursery'];

    return this._dictionariesService.fetchSpecializations().pipe(
      tap((_specializations) => {
        const filteredSpec = _specializations.filter((spec) => !excludedSpecializations.includes(spec.name));

        patchState({
          loading: false,
          specializations: filteredSpec,
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateFetchCategories)
  public fetchWebinarCategories({ patchState, getState }: StateContext<AppStateModel>): Observable<IWebinarCategory[]> {
    if (getState().categories) {
      return of(getState().categories || []);
    }
    patchState({ loading: true });
    return this._dictionariesService.fetchCategories().pipe(
      map((categories) =>
        [...categories].sort((a, b) => {
          const indexA = CATEGORY_ORDER.indexOf(a.id);
          const indexB = CATEGORY_ORDER.indexOf(b.id);
          return indexA - indexB;
        }),
      ),
      tap((sortedCategories) => {
        patchState({ loading: false, categories: sortedCategories });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateFetchWebinarList)
  public fetchWebinarList({ patchState, getState }: StateContext<AppStateModel>): Observable<IWebinarListItem[]> {
    if (getState().webinars) {
      return of(getState().webinars || []);
    }
    patchState({ loading: true });
    return this._dictionariesService.fetchWebinarList({}).pipe(
      map((_webinars) =>
        _webinars.map(
          (webinar): IWebinarListItem => ({
            ...webinar,
            short_description: webinar.short_description
              ? jsonToHtml(webinar.short_description ? webinar.short_description : '{}').replace(
                  new RegExp('/uploads/', 'g'),
                  `${environment.api}/uploads/`,
                )
              : null,
          }),
        ),
      ),
      tap((_webinars) => {
        patchState({
          loading: false,
          webinars: _webinars,
          promotedWebinars: _webinars.filter((webinar) => webinar.promoted),
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }
  @Action(AppStateFetchWebinarLibrary)
  public fetchWebinarLibrary(
    { patchState }: StateContext<AppStateModel>,
    { params }: AppStateFetchWebinarLibrary,
  ): Observable<IWebinarClientListItem[]> {
    patchState({ loading: true });
    return this._userService.fetchClientWebinars({ ...params }).pipe(
      map((_webinars) =>
        _webinars.map(
          (webinarItem): IWebinarClientListItem => ({
            ...webinarItem,
            webinar: {
              ...webinarItem.webinar,
              short_description: webinarItem.webinar.short_description
                ? jsonToHtml(webinarItem.webinar.short_description).replace(
                    new RegExp('/uploads/', 'g'),
                    `${environment.api}/uploads/`,
                  )
                : null,
            },
          }),
        ),
      ),
      tap((_webinars) => {
        patchState({
          loading: false,
          webinarLibrary: _webinars,
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateFetchClientWebinarList)
  public fetchClientWebinarList(
    { patchState, getState }: StateContext<AppStateModel>,
    { payload: { refreshWebinarList } }: AppStateFetchClientWebinarList,
  ): Observable<IWebinarClientListItem[]> {
    if (getState().clientWebinarsLoaded && !refreshWebinarList) {
      return of(getState().clientWebinars);
    }
    patchState({ loading: true });
    return this._userService.fetchClientWebinars().pipe(
      map((_webinars) =>
        _webinars.map(
          (webinarItem): IWebinarClientListItem => ({
            ...webinarItem,
            webinar: {
              ...webinarItem.webinar,
              short_description: webinarItem.webinar.short_description
                ? jsonToHtml(webinarItem.webinar.short_description).replace(
                    new RegExp('/uploads/', 'g'),
                    `${environment.api}/uploads/`,
                  )
                : null,
            },
          }),
        ),
      ),
      tap((clientWebinars) => {
        patchState({ loading: false, clientWebinars, clientWebinarsLoaded: true });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateRegisterLoggedClientToWebinar)
  public registerLoggedClientToWebinar(
    { patchState, dispatch }: StateContext<AppStateModel>,
    { payload: { slug } }: AppStateRegisterLoggedClientToWebinar,
  ): Observable<void> {
    patchState({ loading: true });
    return this._userService.registerLoggedUserToWebinar(slug).pipe(
      concatMap(() => dispatch(new AppStateFetchClientWebinarList({ refreshWebinarList: true }))),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(AppStateFindWebinarDetailsBySlug)
  public findWebinarDetailsBySlug(
    { patchState }: StateContext<AppStateModel>,
    { payload: { slug } }: AppStateFindWebinarDetailsBySlug,
  ): Observable<IWebinarDetails> {
    patchState({ loading: true, webinarSlug: slug, webinar: null });
    return this._dictionariesService.findWebinarDetailsBySlug(slug).pipe(
      tap((webinar) => {
        patchState({
          loading: false,
          webinar: {
            ...webinar,
            description: webinar.description
              ? jsonToHtml(webinar.description ? webinar.description : '{}').replace(
                  new RegExp('/uploads/', 'g'),
                  `${environment.api}/uploads/`,
                )
              : null,
            short_description: webinar.short_description
              ? jsonToHtml(webinar.short_description ? webinar.short_description : '{}').replace(
                  new RegExp('/uploads/', 'g'),
                  `${environment.api}/uploads/`,
                )
              : null,
            speakers: webinar.speakers.map((speaker) => ({
              ...speaker,
              description: speaker.description
                ? jsonToHtml(speaker.description ? speaker.description : '{}').replace(
                    new RegExp('/uploads/', 'g'),
                    `${environment.api}/uploads/`,
                  )
                : null,
            })),
            schedules: webinar.schedules.map((schedule) => ({
              ...schedule,
              speakers: schedule.speakers.map((speaker) => ({
                ...speaker,
                description: speaker.description
                  ? jsonToHtml(speaker.description ? speaker.description : '{}').replace(
                      new RegExp('/uploads/', 'g'),
                      `${environment.api}/uploads/`,
                    )
                  : null,
              })),
            })),
          },
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateFindWebinarDetailsForLoggedClientBySlug)
  public findWebinarDetailsForLoggedClientBySlug(
    { patchState }: StateContext<AppStateModel>,
    { payload: { slug } }: AppStateFindWebinarDetailsForLoggedClientBySlug,
  ): Observable<IWebinarDetailsForLoggedUser> {
    patchState({ loading: true, webinarSlug: slug, webinar: null, webinarDetailsForLoggedUser: null });
    return this._dictionariesService.findWebinarDetailsForLoggedClient(slug).pipe(
      tap((webinarDetailsForLoggedUser) => {
        const transformedWebinar = {
          ...webinarDetailsForLoggedUser.webinar,
          short_description: webinarDetailsForLoggedUser.webinar.short_description
            ? jsonToHtml(webinarDetailsForLoggedUser.webinar.short_description).replace(
                new RegExp('/uploads/', 'g'),
                `${environment.api}/uploads/`,
              )
            : null,
          description: webinarDetailsForLoggedUser.webinar.description
            ? jsonToHtml(webinarDetailsForLoggedUser.webinar.description).replace(
                new RegExp('/uploads/', 'g'),
                `${environment.api}/uploads/`,
              )
            : null,
          speakers: webinarDetailsForLoggedUser.webinar.speakers.map((speaker) => ({
            ...speaker,
            description: speaker.description
              ? jsonToHtml(speaker.description).replace(new RegExp('/uploads/', 'g'), `${environment.api}/uploads/`)
              : null,
          })),
          schedules: webinarDetailsForLoggedUser.webinar.schedules.map((schedule) => ({
            ...schedule,
            speakers: schedule.speakers.map((speaker) => ({
              ...speaker,
              description: speaker.description
                ? jsonToHtml(speaker.description).replace(new RegExp('/uploads/', 'g'), `${environment.api}/uploads/`)
                : null,
            })),
          })),
        };

        patchState({
          loading: false,
          webinarDetailsForLoggedUser: {
            ...webinarDetailsForLoggedUser,
            webinar: transformedWebinar,
          },
          webinar: transformedWebinar,
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(AppStateClearWebinar)
  public clearWebinarSlug({ patchState }: StateContext<AppStateModel>): void {
    patchState({ webinarSlug: null, webinar: null });
  }

  @Action(AppStateDownloadFileOfWebinar)
  public downloadFile(
    { patchState }: StateContext<AppStateModel>,
    { payload: { slug, webinarDownloadDetails } }: AppStateDownloadFileOfWebinar,
  ): Observable<Blob> {
    patchState({ loading: true });
    const extension = webinarDownloadDetails.original_name.split('.').pop();
    return this._dictionariesService.downloadFile(slug, webinarDownloadDetails.id).pipe(
      tap((blob) => {
        if (this.locale === 'pl') {
          saveAs(blob, `${webinarDownloadDetails.name_pl}.${extension}`);
        } else {
          saveAs(blob, `${webinarDownloadDetails.name_en}.${extension}`);
        }
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(AppStateDownloadWebinarCertificate)
  public downloadWebinarCertificate(
    { patchState, getState }: StateContext<AppStateModel>,
    { payload: { slug, language } }: AppStateDownloadWebinarCertificate,
  ): Observable<Blob> {
    const { clientWebinars } = getState();
    const webinar = clientWebinars.find((webinar) => webinar.webinar.slug === slug);
    patchState({ loading: true });
    return this._dictionariesService.downloadCertificate(slug).pipe(
      tap((blob) => {
        const webinarTitle = webinar?.webinar.title;
        const certificateString = language === LanguageEnum.PL ? 'certyfikat' : 'certificate';
        saveAs(blob, `${webinarTitle ? `${snakeCase(webinarTitle)}_${certificateString}` : certificateString}.pdf`);
      }),
      finalize(() => {
        patchState({ loading: false });
      }),
    );
  }

  @Action(AppStateFetchDermasterclassWebinars)
  public fetchDermasterclassWebinars(
    { patchState }: StateContext<AppStateModel>,
    { params }: AppStateFetchDermasterclassWebinars,
  ): Observable<IWebinarListItem[]> {
    patchState({ loading: true });
    return this._dictionariesService.fetchWebinarList({ ...params, dermaster_class: 1 }).pipe(
      map((_webinars) =>
        _webinars.map(
          (webinar): IWebinarListItem => ({
            ...webinar,
            short_description: webinar.short_description
              ? jsonToHtml(webinar.short_description).replace(
                  new RegExp('/uploads/', 'g'),
                  `${environment.api}/uploads/`,
                )
              : null,
          }),
        ),
      ),
      tap((_webinars) => {
        patchState({
          loading: false,
          dermasterclassWebinars: _webinars,
        });
      }),
      catchError((err) => {
        patchState({ loading: false });
        return throwError(() => err);
      }),
    );
  }
}
